import ScrollToTop from "components/ScrollToTop";
import Forbidden from "pages/Forbidden";
import NotFound from "pages/NotFound";
import React, { lazy, Suspense } from "react";
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom";

import RBACTest from './components/RBACTest';
import RoleGuard from './components/RoleGuard';
import { OwnerProvider } from './contexts/OwnerContext';

// Lazy-loaded pages to reduce initial bundle size
const AdminDashboard = lazy(() => import('./pages/admin-dashboard'));
const AdminInvoiceManagement = lazy(() => import('./pages/admin-invoice-management'));
const AdminLeaseManagement = lazy(() => import('./pages/admin-lease-management'));
const AdminMaintenanceTickets = lazy(() => import('./pages/admin-maintenance-tickets'));
const AdminTenantCreate = lazy(() => import('./pages/admin-tenant-create'));
const AdminTenantManagement = lazy(() => import('./pages/admin-tenant-management'));
const TenantDashboard = lazy(() => import('./pages/tenant-dashboard'));
const AdminProfileSettings = lazy(() => import('./pages/admin-profile-settings'));
const AdminPropertyManagement = lazy(() => import('./pages/admin-property-management'));
const TenantProfileSettings = lazy(() => import('./pages/tenant-profile-settings'));
const AdminReports = lazy(() => import('./pages/admin-reports'));
const AdminSettingsPanel = lazy(() => import('./pages/admin-settings-panel'));
const PingLogin = lazy(() => import('./pages/dev/ping-login'));
const TenantBilling = lazy(() => import('./pages/tenant-billing'));
const TenantPaymentSuccess = lazy(() => import('./pages/tenant-payment-success'));
const AdminPaymentSuccess = lazy(() => import('./pages/admin-payment-success'));
const TenantLogin = lazy(() => import('./pages/tenant-login'));
const TenantForgotPassword = lazy(() => import('./pages/tenant-forgot-password'));
const TenantResetPassword = lazy(() => import('./pages/tenant-reset-password'));
const TenantCreateAccount = lazy(() => import('./pages/tenant-create-account'));
const TenantEmailConfirmed = lazy(() => import('./pages/tenant-email-confirmed'));
const TenantMaintenance = lazy(() => import('./pages/tenant-maintenance'));
const TenantHelp = lazy(() => import('./pages/tenant-help'));
const TenantRegister = lazy(() => import('./pages/tenant-register'));
const TenantDocuments = lazy(() => import('./pages/tenant/Documents'));
const SignLease = lazy(() => import('./pages/tenant/SignLease'));
const AdminTenantDocuments = lazy(() => import('./pages/admin/TenantDocuments'));
const AdminTenantLease = lazy(() => import('./pages/admin/TenantLease'));
const StatusPage = lazy(() => import('./pages/Status'));
const SelectOwner = lazy(() => import('./pages/SelectOwner'));
const TestConnection = lazy(() => import('./pages/test-connection'));

const Routes = () => {
  return (
    <>
      <ScrollToTop />
      <RouterRoutes>
                {/* Public Routes */}
                <Route path="/tenant-login" element={<Suspense fallback={null}><TenantLogin /></Suspense>} />
                <Route path="/admin-login" element={<Suspense fallback={null}><TenantLogin /></Suspense>} />
                <Route path="/tenant-forgot-password" element={<Suspense fallback={null}><TenantForgotPassword /></Suspense>} />
                <Route path="/tenant-reset-password" element={<Suspense fallback={null}><TenantResetPassword /></Suspense>} />
                <Route path="/tenant-create-account" element={<Suspense fallback={null}><TenantCreateAccount /></Suspense>} />
                <Route path="/tenant-email-confirmed" element={<Suspense fallback={null}><TenantEmailConfirmed /></Suspense>} />
                <Route path="/tenant/register" element={<Suspense fallback={null}><TenantRegister /></Suspense>} />
                
                {/* Redirect owner-scoped login routes to public login (login shouldn't have owner prefix) */}
                <Route path="/o/:ownerSlug/admin-login" element={<Navigate to="/admin-login" replace />} />
                <Route path="/o/:ownerSlug/tenant-login" element={<Navigate to="/tenant-login" replace />} />
                
                {/* Backward compatibility redirects */}
                <Route path="/forgot-password" element={<Navigate to="/tenant-forgot-password" replace />} />
                <Route path="/register" element={<Navigate to="/tenant-create-account" replace />} />
                
                {/* Root redirect - redirect to appropriate dashboard based on user role */}
                <Route path="/" element={<Navigate to="/tenant-login" replace />} />
                
                {/* Owner selection page */}
                <Route path="/select-owner" element={<Suspense fallback={null}><SelectOwner /></Suspense>} />
                
                {/* Owner-scoped Admin Routes (/o/:ownerSlug/*) */}
                <Route 
                  path="/o/:ownerSlug/admin-dashboard" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminDashboard /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-maintenance-tickets" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminMaintenanceTickets /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-property-management" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminPropertyManagement /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-lease-management" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminLeaseManagement /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-tenant-create" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'owner']}>
                        <Suspense fallback={null}><AdminTenantCreate /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-payment-success" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminPaymentSuccess /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-tenant-management" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminTenantManagement /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-invoice-management" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminInvoiceManagement /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-profile-settings" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminProfileSettings /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-settings-panel" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'owner']}>
                        <Suspense fallback={null}><AdminSettingsPanel /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin-reports" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminReports /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                
                {/* Tenant-only Routes */}
                <Route 
                  path="/tenant-dashboard" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantDashboard /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant-maintenance" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantMaintenance /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant-billing" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantBilling /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant-payment-success" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantPaymentSuccess /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant-profile-settings" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantProfileSettings /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant-help" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantHelp /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant/documents" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantDocuments /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant/sign-lease" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><SignLease /></Suspense>
                    </RoleGuard>
                  }
                />
                
                {/* Admin-only Routes */}
                <Route 
                  path="/admin-dashboard" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager']}>
                        <Suspense fallback={null}><AdminDashboard /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin-maintenance-tickets" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager']}>
                        <Suspense fallback={null}><AdminMaintenanceTickets /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin-property-management" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager']}>
                        <Suspense fallback={null}><AdminPropertyManagement /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin-lease-management" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager']}>
                        <Suspense fallback={null}><AdminLeaseManagement /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin-tenant-create" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'owner']}>
                        <Suspense fallback={null}><AdminTenantCreate /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin-tenant-management" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminTenantManagement /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin-invoice-management" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager']}>
                        <Suspense fallback={null}><AdminInvoiceManagement /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin-profile-settings" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager']}>
                        <Suspense fallback={null}><AdminProfileSettings /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin-settings-panel" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin']}>
                        <Suspense fallback={null}><AdminSettingsPanel /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                
                {/* Redirect old routes to correct ones */}
                <Route path="/admin-profile" element={<Navigate to="/admin-profile-settings" replace />} />
                <Route path="/o/:ownerSlug/admin-billing" element={<Navigate to="/o/:ownerSlug/admin-invoice-management" replace />} />
                <Route path="/admin-billing" element={<Navigate to="/admin-invoice-management" replace />} />
                <Route path="/tenant-profile" element={<Navigate to="/tenant-profile-settings" replace />} />
                
                {/* Reports Route */}
                <Route 
                  path="/admin-reports" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager']}>
                        <Suspense fallback={null}><AdminReports /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/o/:ownerSlug/admin/tenant-documents" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminTenantDocuments /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin/tenant-documents" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminTenantDocuments /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                {/* Tenant Lease Route - supports both owner-scoped and base paths */}
                <Route 
                  path="/o/:ownerSlug/admin-tenant-lease" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminTenantLease /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                <Route 
                  path="/admin/tenant-lease" 
                  element={
                    <OwnerProvider>
                      <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                        <Suspense fallback={null}><AdminTenantLease /></Suspense>
                      </RoleGuard>
                    </OwnerProvider>
                  }
                />
                
                {/* Test Routes */}
                <Route path="/rbac-test" element={<RBACTest />} />
                <Route path="/dev/ping-login" element={<Suspense fallback={null}><PingLogin /></Suspense>} />
                <Route path="/status" element={<Suspense fallback={null}><StatusPage /></Suspense>} />
                <Route path="/test-connection" element={<Suspense fallback={null}><TestConnection /></Suspense>} />
                
                {/* Error Pages */}
                <Route path="/403" element={<Forbidden />} />
                <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </>
  );
};

export default Routes;